import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';

import { makePair, Pair } from '../../../../../adl-gen/sys/types';
import {
  AnswerIndex,
  AnswerScores,
  I18nContent,
  makeOrderedResponse,
  Score
} from '../../../../../adl-gen/totalinteraction/futurefit/db';
import { LangCode, withI18n, WithI18nProps } from '../../../../../internationalization/i18n-hoc';
import { PlusIcon } from '../../../../../ui/common/icons';
import { Button } from '../../../../../ui/widgets/button/button';
import { SelectBox, SelectOption } from '../../../../../ui/widgets/select/select';
import { Table, TableCell, TableRow } from '../../../../../ui/widgets/table/table';
import {
  FieldState,
  FormValue,
  newIntegerFieldState,
  withValueCallback
} from '../../../../../ui/widgets/text-input/text-field';
import { uniqueId, WithId } from '../../../../../util/utils';
import { AnswerType, isEmpty, TextInputObserver } from '../../../common/editor';

import { OrderedResponseScoreSetEditor } from './ordered-response-score-set-editor';

const styles = require('../../../common/editor.css');
const listStyles = require('../../../common/list.css');
const fonts = require('../../../../../ui/common/fonts.css');

/// TODO Amalgamate/consolidate all these type defs and interface defs
interface OrderedResponseInputScore {
  key: number;
  value: Pair<AnswerIndex[], Score>;
}

interface OrderedResponseInputScores {
  answerPermsScores: OrderedResponseInputScore[];
  defaultScore: FieldState<number>;
}

interface ScoringInput {
  kind: AnswerType;
  value: OrderedResponseInputScores;
}

export interface OrderedResponseEditorProps extends WithI18nProps {
  debug?: boolean;
  lang?: LangCode;
  name: string;
  content: I18nContent;
  answers: WithId<I18nContent>[];
  scoring?: AnswerScores;

  postUpdates(answers: AnswerScores): void;

  onCancel(): void;
}

/**
 * Base component to create/update OrderedResponse answers.
 * This one is not like the other types:
 * 1. The structure is
 *    [score0, score1, score2, ...] -> [ {v1: ordered-array-1-all-answers, v2: score}, {v1: ordered-array-2-all-answers, v2: score}, ...]
 * 2. There is no correlation between # of answers and # of scores, or array index of answers and v1 of scores.
 *    # of scores can exceed # of answers. Each value v1 however must be the same size as the total # of answers.
 *    It's possible to have no scores at all.
 * 3. The default score is mandatory as there are potentially n! scores for n answers and they can't realistically all be enumerated.
 * 4. NB: No effort made here to preserve original order of answers, since that
 *        is what is being scored in the first place.
 */
@observer
class OrderedResponseEditor0 extends React.Component<OrderedResponseEditorProps> {
  @observable inputScores: ScoringInput;

  @observable lang: LangCode;

  constructor(props: Readonly<OrderedResponseEditorProps>) {
    super(props);
    this.lang = props.lang ? props.lang : props.currentLangCode();
    const { scoring } = props;

    const scores: OrderedResponseInputScore[] = [];

    if (scoring) {
      if (scoring.kind !== 'orderedResponse') {
        // Something has gone wrong
        throw new Error(`OrderedResponseEditor: incompatible answer type ${scoring.kind}`);
      }

      scoring.value.answerPermsScores.map(score => {
        const inputScore = {
          key: uniqueId(),
          value: makePair({
            v1: score.v1,
            v2: score.v2
          })
        };
        scores.push(inputScore);
      });
    }

    const defaultScore = withValueCallback(newIntegerFieldState(0, 100), this.onChangeDefaultScore);

    this.inputScores = {
      kind: 'orderedResponse',
      value: {
        answerPermsScores: scores,
        defaultScore
      }
    };

    if (scoring && scoring.kind === 'orderedResponse') {
      this.inputScores.value.defaultScore.onChange(`${scoring.value.defaultScore}`);
    }
  }

  @action
  changeLang = (opt: SelectOption) => {
    this.lang = opt.value as LangCode;
  };

  LangSelector = observer(() => {
    const { i18nOptions } = this.props;
    const onChange = this.changeLang;
    const options = i18nOptions();
    return (
      <SelectBox
        options={options}
        defaultValue={options.find(option => (option.value as LangCode) === this.lang)}
        onChange={onChange}
        title={'Language'}
        xsSpan={2}
      />
    );
  });

  DefaultScoreObserver = observer(() =>
    TextInputObserver({
      field: this.inputScores.value.defaultScore,
      type: 'number',
      title: 'Default score',
      placeholder: 'eg. 33',
      xsSpan: 1,
      debug: this.props.debug
    })
  );

  @computed
  get inputIsValid(): boolean {
    let isValid: boolean = true;
    // const { answers } = this.props
    // const { defaultScore, answerPermsScores } = this.inputScores.value
    // isValid = defaultScore.getValid() !== undefined &&
    //       !isEmpty(defaultScore) &&
    //       isNumeric(defaultScore.value())
    // if (isValid) {
    //   isValid = answerPermsScores.every(score => {
    //     return score.v1.length === answers.length &&
    //            score.v1.every(item => item >= 0 && item < answers.length) &&
    //            score.v2.getValid() !== undefined &&
    //            !isEmpty(score.v2) &&
    //            isNumeric(score.v2.value())
    //   })
    // }
    return isValid;
  }

  @action
  onChangeDefaultScore = (_score: FormValue<number>) => {
    /// Post
    this.onSave();
  };

  @action
  onSave = () => {
    const { postUpdates } = this.props;
    if (this.inputIsValid) {
      let defaultScore = 33;
      if (isEmpty(this.inputScores.value.defaultScore)) {
        defaultScore = 0;
      } else {
        const ds = this.inputScores.value.defaultScore.getValid();
        if (ds !== undefined) {
          defaultScore = ds;
        }
      }

      /// AddAnswerSet and RemoveAnswerSet may leave a sparse array - ?
      const scores = this.inputScores.value.answerPermsScores.reduce((acc: Pair<number[], number>[], next) => {
        acc.push(makePair({ v1: next.value.v1, v2: next.value.v2 }));
        return acc;
      }, []);

      const updates = makeOrderedResponse({
        answerPermsScores: scores,
        defaultScore
      });

      postUpdates({
        kind: 'orderedResponse',
        value: updates
      });
    }
  };

  @action
  onScoreSetUpdate = (index: number) => (newScore: Pair<AnswerIndex[], Score>) => {
    let changed = false;
    const oldScore = this.inputScores.value.answerPermsScores[index];
    if (oldScore.value.v1.length === newScore.v1.length && oldScore.value.v1.every((x, i) => x === newScore.v1[i])) {
      if (oldScore.value.v2 !== newScore.v2) {
        // perm unchanged, but score changed: update
        this.inputScores.value.answerPermsScores[index].value.v2 = newScore.v2;
        changed = true;
      }
    } else {
      // Perm changed (and maybe score), update new in place. Don't use splice() here
      this.inputScores.value.answerPermsScores[index].value = newScore;
      //  const removed =
      // this.inputScores.value.answerPermsScores.splice(index, 1, { key: uniqueId(), value: newScore })
      changed = true;
    }
    if (changed) {
      this.onSave();
    }
  };

  @action
  onAddAnswerSet = () => {
    const { answers } = this.props;
    if (this.inputScores.kind === 'orderedResponse') {
      // Set a default or otherwise won't get added cos it's empty
      const newAnswerSet = makePair({ v1: answers.map((_v, i) => i), v2: 0 });
      this.inputScores.value.answerPermsScores.push({ key: uniqueId(), value: newAnswerSet });
    }
    this.onSave();
  };

  @action
  onRemoveAnswerSet = (key: number) => {
    const indexToRemove = this.inputScores.value.answerPermsScores.findIndex(aps => aps.key === key);
    if (indexToRemove >= 0) {
      // const removed =
      this.inputScores.value.answerPermsScores.splice(indexToRemove, 1);
      this.onSave();
    }
  };

  AddButton = () => {
    return (
      <div className={styles.buttonList}>
        <Button
          // className={styles.button}
          onClick={async () => this.onAddAnswerSet()}
          Icon={PlusIcon}
          size="small"
          // disabled={}
        >
          Add
        </Button>
      </div>
    );
  };

  Edit = () => {
    const { answers, content, t } = this.props;
    return (
      <div className={styles.page}>
        <div className={styles.body}>
          <div className={styles.row}>
            <this.LangSelector />
          </div>
          <div className={styles.row}>
            <span className={fonts.caption}>{t(content, this.lang)}</span>
          </div>
          <div className={styles.row}>
            <this.DefaultScoreObserver />
          </div>
          <Table className={listStyles.noBorder}>
            <TableRow>
              {this.inputScores.value.answerPermsScores.map((score, index) => {
                /// Do not set the React key attribute to just the index in this array as it
                /// is not updated following a delete.
                /// However use the index in the onScoreSetUpdate closure as otherwise
                /// can cause runaway loop (constructor calls postUpdates)
                return (
                  <TableCell key={score.key}>
                    <div className={styles.row}>
                      <OrderedResponseScoreSetEditor
                        answers={answers}
                        answerPermScore={score.value}
                        postUpdates={this.onScoreSetUpdate(index)}
                      />
                      <Button size="small" children="Delete" onClick={async () => this.onRemoveAnswerSet(score.key)} />
                    </div>
                  </TableCell>
                );
              })}
            </TableRow>
          </Table>
        </div>
      </div>
    );
  };

  render() {
    const { name } = this.props;
    const title = `Answers for question ${name} (${this.inputScores.kind})`;
    return (
      <React.Fragment>
        <div>{title}</div>
        <this.AddButton />
        <this.Edit />
      </React.Fragment>
    );
  }
}

export const OrderedResponseEditor = withI18n(OrderedResponseEditor0);



// WEBPACK FOOTER //
// ./src/admin/ui/components/editors/answers-scoring-editors/ordered-response-editor.tsx